<template>
	<div
		class="form__set"
		:class="groupClass"
	>
		<template v-if="type === 'select'">
			<label
				v-if="label && !hideLabel"
				:for="inputId"
				class="form__label"
			>
				{{ label }}
			</label>
			<p
				v-if="subLabel"
				class="form__sub-label"
			>
				{{ subLabel }}
			</p>
			<div class="form__icon form__icon--down form__input-wrapper">
				<select
					:id="inputId"
					:value="value"
					:name="inputId"
					class="form__select"
					:disabled="disabled"
					:aria-describedby="( subLabel ? inputId + '-description ' : '' ) + ( errors ? inputId + '-error' : '' )"
					:class="( inputClass ) + ( errors ? ' form__select--error' : '' )"
					@input="$emit('update:value', $event.target.value)"
				>
					<option
						v-for="( option, index ) in options"
						:key="index + '-' + inputId"
						:value="option.value"
					>
						{{ option.label }}
					</option>
				</select>
			</div>
		</template>
		<template v-else-if="type === 'textarea'">
			<label
				v-if="label && !hideLabel"
				:for="inputId"
				class="form__label"
			>
				{{ label }}
			</label>
			<p
				v-if="subLabel"
				class="form__sub-label"
			>
				{{ subLabel }}
			</p>
			<p
				v-if="charLimit"
				class="form__character-limit"
			>
				{{ remaining_character_count }} characters left
			</p>
			<textarea
				:id="inputId"
				:value="value"
				:type="type"
				:name="name"
				class="form__input"
				rows="6"
				:maxlength="charLimit"
				:disabled="disabled"
				:aria-describedby="( subLabel ? inputId + '-description ' : '' ) + ( inputId + '-max-length-description ' ) + ( errors ? inputId + '-error' : '' )"
				:class="( inputClass ) + ( errors ? ' form__input--error' : '' )"
				@input="$emit( 'update:value', $event.target.value )"
			/>
		</template>
		<template v-else-if="type === 'checkbox'">
			<label class="checkbox-wrapper">
				<input
					:id="inputId"
					v-model="checked_items"
					class="checkbox"
					:class="( inputClass ) + ( errors ? ' checkbox--error' : '' )"
					:value="value"
					type="checkbox"
					:name="name"
					:disabled="disabled"
					:aria-describedby="( subLabel ? inputId + '-description ' : '' ) + ( errors ? inputId + '-error' : '' )"
				>
				<span class="checkbox__label">{{ label }}</span>
			</label>
		</template>
		<template v-else-if="type === 'dateinputs'">
			<label
				v-if="label && !hideLabel"
				:for="inputId"
				class="form__label"
			>
				{{ label }}
			</label>
			<p
				v-if="subLabel"
				class="form__sub-label"
			>
				{{ subLabel }}
			</p>
			<InputDateComponent
				:input-id="inputId"
				:value="value"
				:error="null !== errors && errors.length > 0"
				@updated="new_value => $emit( 'update:value', new_value )"
			/>
		</template>
		<template v-else-if="type === 'multiselect'">
			<label
				v-if="label && !hideLabel"
				:for="inputId"
				class="form__label"
			>
				{{ label }}
			</label>
			<p
				v-if="subLabel"
				class="form__sub-label"
			>
				{{ subLabel }}
			</p>
			<div
				v-for="( multiselect_value, key ) in multiselect_inputs"
				:key="key"
				class="form__input-with-remove"
			>
				<div class="form__icon form__icon--down">
					<select
						:id="inputId + '_' + key"
						:key="'multiselect-input-' + key"
						ref="multiselect_input_refs"
						:value="multiselect_value"
						:name="name"
						class="form__select"
						:disabled="disabled"
						:aria-describedby="( subLabel ? inputId + '-description ' : '' ) + ( errors ? inputId + '-error' : '' )"
						:class="( inputClass ) + ( errors ? ' form__select--error' : '' )"
						@input="$emit('update:value', $event.target.value)"
					>
						<option
							v-for="( option, option_key ) in options"
							:key="option_key"
							:value="option_key"
						>
							{{ option }}
						</option>
					</select>
				</div>
				<button
					:key="'multiselect-remove-' + key"
					type="button"
					class="icon icon--close"
					aria-label="Remove this input"
					@click="removeMultiselectInput( key )"
				/>
			</div>
			<button
				type="button"
				class="button button--small"
				@click="addMultiselectInput"
			>
				<span class="icon icon--plus" />
				{{ addText }}
			</button>
		</template>
		<template v-else-if="type === 'checkboxes-dropdown'">
			<label
				v-if="label && !hideLabel"
				:for="inputId"
				class="form__label"
			>
				{{ label }}
			</label>
			<p
				v-if="subLabel"
				class="form__sub-label"
			>
				{{ subLabel }}
			</p>
			<CheckboxesDropdownComponent
				:id="inputId"
				:label="label"
				:placeholder="placeholder"
				:options="options"
				:unfiltered-options="unfilteredOptions"
				:query="filterSearch"
				:selection="activeValues"
				:stretch="true"
				:search-disabled="false"
				:loading="false"
				:selection-label="selectionLabel"
				@select="new_value => $emit( 'update:value', new_value )"
				@update:query="handleSearchInputChange"
			/>
		</template>
		<template v-else-if="type === 'button-dropdown'">
			<label
				v-if="label && !hideLabel"
				:for="inputId"
				class="form__label"
			>
				{{ label }}
			</label>
			<p
				v-if="subLabel"
				class="form__sub-label"
			>
				{{ subLabel }}
			</p>
			<ButtonDropdownComponent
				:id="inputId"
				:label="label"
				:placeholder="placeholder"
				:options="options"
				:query="filterSearch"
				:stretch="true"
				:search-disabled="false"
				:loading="false"
				@select="new_value => $emit( 'update:value', new_value )"
				@update:query="handleSearchInputChange"
			/>
		</template>
		<template v-else-if="type === 'multilevel-dropdown'">
			<label
				v-if="label && !hideLabel"
				:for="inputId"
				class="form__label"
			>
				{{ label }}
			</label>
			<p
				v-if="subLabel"
				class="form__sub-label"
			>
				{{ subLabel }}
			</p>
			<MultiLevelDropdownComponent
				:id="inputId"
				:label="label"
				:placeholder="placeholder"
				:options="options"
				:stretch="true"
				:search-disabled="false"
				:loading="false"
				@select="new_value => $emit( 'update:value', new_value )"
				@refresh:project-locations="( value, handle ) => emitUpdateLocations( value, handle )"
			/>
		</template>
		<template v-else>
			<label
				v-if="label && !hideLabel"
				:for="inputId"
				class="form__label"
			>
				{{ label }}
			</label>
			<p
				v-if="subLabel"
				class="form__sub-label"
			>
				{{ subLabel }}
			</p>
			<input
				:id="inputId"
				:value="value"
				:type="type"
				:name="name"
				:placeholder="placeholder"
				:disabled="disabled"
				:aria-describedby="( subLabel ? inputId + '-description ' : '' ) + ( errors ? inputId + '-error' : '' )"
				class="form__input"
				:class="( inputClass ) + ( errors ? ' form__input--error' : '' )"
				@input="$emit( 'update:value', $event.target.value )"
				@blur="$emit( 'blur' )"
			>
		</template>
		<template v-if="errors">
			<p
				v-for="( error, index ) in errors"
				:id="inputId + '-error'"
				:key="inputId + '-error-' + index"
				class="form__error"
			>
				{{ error }}
			</p>
		</template>
	</div>
</template>

<script setup>
import InputDateComponent from './InputDateComponent.vue';
import CheckboxesDropdownComponent from './CheckboxesDropdownComponent.vue';
import ButtonDropdownComponent from './ButtonDropdownComponent.vue';
import MultiLevelDropdownComponent from './MultiLevelDropdownComponent.vue';
import { ref, computed, defineProps, defineEmits, watch, onMounted, nextTick } from 'vue';

const props = defineProps( {
	value: {
		required: true,
		type: [ String, Number, Boolean, Array ]
	},
	label: {
		required: false,
		type: String,
		default: null
	},
	subLabel: {
		required: false,
		type: String,
		default: null
	},
	inputId: {
		required: true,
		type: String
	},
	type: {
		required: true,
		type: String
	},
	name: {
		required: true,
		type: String
	},
	placeholder: {
		required: false,
		type: String,
		default: ''
	},
	disabled: {
		required: false,
		type: Boolean,
		default: false
	},
	options: {
		required: false,
		type: Array,
		default: null
	},
	required: {
		required: true,
		type: Boolean
	},
	errors: {
		required: false,
		type: Array,
		default: null
	},
	inputClass: {
		required: false,
		type: String,
		default: ''
	},
	groupClass: {
		required: false,
		type: String,
		default: ''
	},
	charLimit: {
		required: false,
		type: Number,
		default: null
	},
	addText: {
		required: false,
		type: String,
		default: null
	},
	activeValues: {
		required: true,
		type: Array
	},
	filterKey: {
		required: false,
		type: String,
		default: ''
	},
	filterSearch: {
		required: false,
		type: String,
		default: ''
	},
	selectionLabel: {
		required: false,
		type: String,
		default: null
	}
} );

import { useNewProjectStore } from '../stores/new-project.js';

const filters_store = useNewProjectStore();

const {
	setFilterSearchQuery
} = filters_store;

const emit = defineEmits( [ 'update:value', 'blur', 'refresh:project-locations' ] );

const checked_items = ref( [] );
const primary_checked_items = ref( [] );
const multiselect_inputs = ref( [] );
const multiselect_input_refs = ref( [] );

const remaining_character_count = computed( () => {
	const current_length = ( null !== props.value ) ? props.value.length : 0;

	if ( !props.charLimit ) {
		return null;
	}

	return props.charLimit - current_length;
} );

watch( checked_items, () => {
	if ( false === Array.isArray( checked_items.value ) ) {
		emit( 'update:value', checked_items.value );

		return;
	}

	if ( checked_items.value.length > 0 ) {
		primary_checked_items.value = [];
		emit( 'update:value', checked_items.value );
	} else {
		emit( 'update:value', primary_checked_items.value );
	}
} );

watch( primary_checked_items, () => {
	if ( primary_checked_items.value.length > 0 ) {
		checked_items.value = [];
		emit( 'update:value', primary_checked_items.value );
	} else {
		emit( 'update:value', checked_items.value );
	}
} );

watch( multiselect_inputs, () => {
	emit( 'update:value', multiselect_inputs.value );
}, { deep: true } );

onMounted( () => {
	if ( props.type === 'checkbox' ) {
		checked_items.value = props.value;
	} else if ( props.type === 'multiselect' ) {
		multiselect_inputs.value = props.value;
	}
	return;
} );

function addMultiselectInput() {
	multiselect_inputs.value.push( '' );

	nextTick( () => {
		const new_input_index = multiselect_inputs.value.length - 1;
		multiselect_input_refs.value[new_input_index].focus();
	} );
}

function removeMultiselectInput( index ) {
	multiselect_inputs.value.splice( index, 1 );
}

const handleSearchInputChange = async( value ) => { // eslint-disable-line snakecasejs/snakecasejs
	setFilterSearchQuery( value, props.filterKey );
};

function emitUpdateLocations( value, handle ) {
	emit( 'refresh:project-locations',  value, handle );
}

</script>
